import React, { useEffect, useMemo, useRef, useState } from 'react';

import Button from 'components/shared/Button';
import Slider from 'components/shared/Slider';
import Toast from 'components/shared/Toast';

import { useResizeObserver } from 'utils/hooks';
import { useAppDispatch, useAppSelector } from 'utils/hooks';

import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';
import stage from 'domains/stage';
import rooms from 'domains/wall_preview_rooms';
import ui from 'domains/ui';
import textNodes from 'domains/text_nodes';

import { selectZoomedPPI } from 'selectors/ppi';
import { wallPreviewBoundsCheck } from 'thunks/arrangement_thunks';

import Canvas from './Canvas';
import { useArrangement, useDragMovement } from './hooks';

function Stage() {
  const dispatch = useAppDispatch();
  const activeCanvasId = useAppSelector(canvases.selectors.activeId);
  const activeCatalog = useAppSelector(activeEditor.selectors.catalog);
  const activeSubcatalog = useAppSelector(activeEditor.selectors.subcatalog);
  const activeTextNodeId = useAppSelector(textNodes.selectors.activeNode);
  const canvasIds = useAppSelector(canvases.selectors.selectIds);
  const ppi = useAppSelector(selectZoomedPPI);
  const arrangementPosition = useAppSelector(canvases.selectors.arrangementPosition);
  const isCartProduct = useAppSelector(activeEditor.selectors.isCartProduct);

  const whichWallPreview = useAppSelector(rooms.selectors.whichWallPreview);

  const screenDimensions = useAppSelector(stage.selectors.screenDimensions);
  const stageZoom = useAppSelector(stage.selectors.zoom);
  const wallPreviewDimensions = useAppSelector(stage.selectors.wallPreviewDimensions);
  const wallPreviewEnabled = useAppSelector(stage.selectors.wallPreviewActive);

  const toastMessage = useAppSelector(stage.selectors.toast);

  const stageElem = useRef<HTMLDivElement>(null);

  const [editingCanvasId, setEditingCanvasId] = useState<string | null>(null);
  const [wasDragging, setWasDragging] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const innerStageDimensions = useResizeObserver('app_stage_inner');
  const arrangement = useArrangement();

  const hasArrangement = useMemo(() => !!activeSubcatalog?.arrangement, [activeSubcatalog]);

  const showStageTools = useMemo(
    () => !wallPreviewEnabled && !isCartProduct && hasArrangement,
    [wallPreviewEnabled, isCartProduct, hasArrangement]
  );

  const arrMove = useDragMovement({
    selector: '#app_stage',
    exclude: ['.canvas', '.stage-tools'],
    disable: !showStageTools,
    onComplete: ({ x, y }) => {
      dispatch(canvases.actions.moveArrangement({ dx: x, dy: y }));
    },
  });

  useEffect(() => {
    if (toastMessage) {
      setShowToast(true);
    }
  }, [toastMessage]);

  // from wallpreview component, probably better place for this
  const isFramesClass = useMemo(() => {
    let className = 'not-frames';

    if (
      activeCatalog &&
      (activeCatalog.slug === 'FramedPrints' || activeCatalog.slug === 'woodprints')
    ) {
      className = 'frames';
    }

    return className;
  }, [activeCatalog]);

  // Set the stage dimensions when resize observer hook updates
  useEffect(() => {
    dispatch(stage.actions.setScreenDimensions(innerStageDimensions));
  }, [innerStageDimensions, dispatch]);

  // When wall preview is on, check that the arrangement is within the bounds when screen/wall preview sizes change
  useEffect(() => {
    if (wallPreviewEnabled) {
      dispatch(wallPreviewBoundsCheck());
    }
  }, [wallPreviewEnabled, screenDimensions, dispatch]);

  const wallPreviewBoundingContainerStyle = useMemo(() => {
    if (!wallPreviewEnabled || !wallPreviewDimensions || !whichWallPreview?.blobSrc) {
      return {
        width: screenDimensions.width,
        height: screenDimensions.height,
      };
    }

    return {
      backgroundImage: `url(${whichWallPreview.blobSrc})`,
      height: wallPreviewDimensions.height,
      width: wallPreviewDimensions.width,
    };
  }, [wallPreviewEnabled, whichWallPreview, wallPreviewDimensions, screenDimensions]);

  const arrangementWrapperStyle: React.CSSProperties = useMemo(() => {
    const top = arrangementPosition.y * ppi + arrMove.y;
    const left = arrangementPosition.x * ppi + arrMove.x;
    const width = arrangement.width * ppi;
    const height = arrangement.height * ppi;

    return {
      position: 'absolute',
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
  }, [arrangement, arrangementPosition, ppi, arrMove]);

  const handleStageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const canvasElements = Array.from(document.querySelectorAll('.canvas'));
    const clickInCanvas = canvasElements.some(
      (canvas) => e.target instanceof Element && canvas.contains(e.target)
    );
    if (!clickInCanvas && !wasDragging) {
      setEditingCanvasId(null);
    }

    if (wasDragging) {
      setWasDragging(false);
    }

    const textnodes = Array.from(document.querySelectorAll('.text_node--svg'));
    const clickInTextNode = textnodes.some(
      (textnode) => e.target instanceof Element && textnode.contains(e.target)
    );
    if (!clickInTextNode) {
      if (activeTextNodeId) {
        dispatch(ui.actions.setSidebarComponent('default'));
        dispatch(textNodes.actions.setActiveTextNodeUid(''));
      }
    }
  };

  function handleZoom(value: string) {
    dispatch(stage.actions.setStageZoom(parseFloat(value)));
  }

  function handleAutoFit() {
    dispatch(stage.actions.autofit());
  }

  useEffect(() => {
    if (!stageElem.current) return;

    if (arrMove.x !== 0 || arrMove.y !== 0) {
      stageElem.current.style.cursor = 'grabbing';
      setWasDragging(true);
    } else {
      stageElem.current.style.cursor = showStageTools ? 'grab' : 'default';
    }
  }, [arrMove, showStageTools]);

  const Canvases = useMemo(() => {
    return canvasIds.map((canvasId) => {
      return (
        <Canvas
          canvasId={canvasId as string}
          key={canvasId}
          isEditingCanvas={canvasId === editingCanvasId && canvasId === activeCanvasId}
          setEditingCanvasId={setEditingCanvasId}
        />
      );
    });
  }, [activeCanvasId, canvasIds, editingCanvasId]);

  return (
    // TODO: Any better way to handle this?
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      id="app_stage"
      ref={stageElem}
      className={`app_stage ${wallPreviewEnabled ? `wall-preview-enabled ${isFramesClass}` : ''}`}
      onClick={handleStageClick}
      style={{ cursor: hasArrangement ? 'grab' : 'default' }}
    >
      {showStageTools && (
        <div className="stage-tools">
          <Button
            type="icon"
            icon="crosshairs"
            size="lg"
            a11yText="Fit products to screen"
            appearance="themed"
            onClick={handleAutoFit}
          />
          <Slider
            id="zoom-stage"
            min="0.05"
            max="2"
            value={stageZoom}
            step="0.01"
            onChange={handleZoom}
            title="Zoom all products"
          />
        </div>
      )}

      <div id="app_stage_inner" className="app_stage_inner">
        {toastMessage && showToast && (
          <Toast
            message={toastMessage}
            animationIn="slideInLeft"
            onClose={() => setShowToast(false)}
          />
        )}

        <div className="arrangement-container" style={arrangementWrapperStyle}>
          {Canvases}
        </div>
        <div
          className="wall-preview-bounding-container"
          style={wallPreviewBoundingContainerStyle}
        />
      </div>
    </div>
  );
}

export default Stage;
